import { Alert, Autocomplete, Button, MenuItem, TextField } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useGoldGamesBrands, useGoldGamesGames } from '../../queries/goldGames';
import type { CashbackBonusFormValues } from '../../schema';
import type { GoldGamesBrand, GoldGamesGame } from '../../@types/goldGames';
import CashbackTierFields, { type CashbackTierProps } from './CashbackTierFields';

type Props = CashbackTierProps & {
  marketplaceId: string;
  currency: string;
};

const CashbackGoldGamesTierRow = ({ marketplaceId, currency, ...props }: Props) => {
  const {
    control,
    setValue,
    formState: { errors, isReady },
  } = useFormContext<CashbackBonusFormValues>();
  const tier = useWatch({ control, name: `freeSpinTiers.${props.index}` });
  const brands = useGoldGamesBrands(marketplaceId);
  const games = useGoldGamesGames(marketplaceId, tier.providerId, currency);
  const game = games.data?.games.find((item) => String(item.id) === tier.gameId);
  const selectedPreset = game?.presets.find(
    (preset) =>
      preset.rounds === tier.freeSpinsAwarded &&
      preset.roundCost === tier.betValuePerSpin &&
      preset.betLevel === tier.goldGames?.betLevel &&
      preset.coinValue === tier.goldGames?.coinValue
  );

  const clearPreset = useCallback(() => {
    setValue(`freeSpinTiers.${props.index}.freeSpinsAwarded`, null, { shouldDirty: true, shouldValidate: true });
    setValue(`freeSpinTiers.${props.index}.betValuePerSpin`, null, { shouldDirty: true, shouldValidate: true });
    setValue(
      `freeSpinTiers.${props.index}.goldGames`,
      { betLevel: 0, coinValue: 0, validityHours: tier.goldGames?.validityHours ?? 24 },
      { shouldDirty: true, shouldValidate: true }
    );
  }, [props.index, setValue, tier.goldGames?.validityHours]);

  const selectGame = (selected: GoldGamesGame | null) => {
    setValue(`freeSpinTiers.${props.index}.gameId`, selected ? String(selected.id) : '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`freeSpinTiers.${props.index}.gameName`, selected?.name ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearPreset();
  };

  const selectProvider = (selected: GoldGamesBrand | null) => {
    setValue(`freeSpinTiers.${props.index}.providerId`, selected ? String(selected.id) : '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`freeSpinTiers.${props.index}.providerName`, selected?.name ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    selectGame(null);
  };

  const selectPreset = (value: string) => {
    const preset = game?.presets.find((item) => String(item.id) === value);
    if (!preset) return;
    const options = { shouldDirty: true, shouldValidate: true };
    setValue(
      `freeSpinTiers.${props.index}.goldGames`,
      {
        betLevel: preset.betLevel,
        coinValue: preset.coinValue,
        validityHours: tier.goldGames?.validityHours ?? 24,
      },
      options
    );
    setValue(`freeSpinTiers.${props.index}.freeSpinsAwarded`, preset.rounds, options);
    setValue(`freeSpinTiers.${props.index}.betValuePerSpin`, preset.roundCost, options);
  };

  const catalogUnavailable = brands.isLoading || brands.isError || games.isError;
  const bettingDisabled = props.disabled || catalogUnavailable || games.isFetching || !game;
  const hasBettingSettings =
    tier.freeSpinsAwarded !== null ||
    tier.betValuePerSpin !== null ||
    !!tier.goldGames?.betLevel ||
    !!tier.goldGames?.coinValue;
  const showSavedSettings = props.disabled && !selectedPreset && tier.betValuePerSpin !== null;
  const savedSettingsLabel = `${tier.freeSpinsAwarded} spins × ${tier.betValuePerSpin} ${currency} (level ${tier.goldGames?.betLevel ?? 'not recorded'}, coin ${tier.goldGames?.coinValue ?? 'not recorded'}) — saved settings`;

  useEffect(() => {
    if (
      isReady &&
      !props.disabled &&
      !catalogUnavailable &&
      games.data &&
      !games.isFetching &&
      hasBettingSettings &&
      !selectedPreset
    ) {
      clearPreset();
    }
  }, [
    isReady,
    props.disabled,
    catalogUnavailable,
    games.data,
    games.isFetching,
    hasBettingSettings,
    selectedPreset,
    clearPreset,
  ]);

  return (
    <>
      {(brands.isError || games.isError) && (
        <Alert
          severity='error'
          action={
            <Button
              onClick={() => {
                void brands.refetch();
                if (tier.providerId) void games.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Unable to load Gold Games providers or games.
        </Alert>
      )}
      <CashbackTierFields {...props} disabled={props.disabled || catalogUnavailable} spinCountReadOnly>
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.providerId`}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={brands.data?.brands ?? []}
              value={brands.data?.brands.find((brand) => String(brand.id) === field.value) ?? null}
              onChange={(_event, selected) => selectProvider(selected)}
              onBlur={field.onBlur}
              getOptionLabel={(brand) => brand.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={props.disabled || catalogUnavailable}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Provider'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.gameId`}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={games.data?.games ?? []}
              value={games.data?.games.find((item) => String(item.id) === field.value) ?? null}
              onChange={(_event, selected) => selectGame(selected)}
              onBlur={field.onBlur}
              getOptionLabel={(item) => item.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={props.disabled || catalogUnavailable || games.isFetching || !tier.providerId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Game'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <TextField
          select
          required
          label='Bet Value per Spin / Preset'
          value={showSavedSettings ? 'saved' : selectedPreset ? String(selectedPreset.id) : ''}
          disabled={bettingDisabled}
          error={!!errors.freeSpinTiers?.[props.index]?.betValuePerSpin}
          helperText={
            errors.freeSpinTiers?.[props.index]?.betValuePerSpin?.message ??
            'Quantity and bet value are defined by the provider preset.'
          }
          onChange={(event) => selectPreset(event.target.value)}
        >
          {showSavedSettings && <MenuItem value='saved'>{savedSettingsLabel}</MenuItem>}
          {(game?.presets ?? []).map((preset) => (
            <MenuItem key={preset.id} value={String(preset.id)}>
              {preset.rounds} spins × {preset.roundCost} {currency} (level {preset.betLevel}, coin {preset.coinValue})
            </MenuItem>
          ))}
        </TextField>
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.goldGames.validityHours`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? 24}
              type='number'
              required
              label='Spin validity (hours after settlement)'
              disabled={bettingDisabled}
              onChange={(event) => field.onChange(Number(event.target.value))}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ??
                "1–720 hours. A new cashback award replaces this player's previous cashback spins for this game."
              }
            />
          )}
        />
      </CashbackTierFields>
    </>
  );
};

export default CashbackGoldGamesTierRow;
